import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Undo, RestartAlt, FastForward, AutoAwesome, KeyboardArrowUp, KeyboardArrowDown,
    DragIndicator, ExpandMore, ChevronRight,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { currentSeason, mapMappings } from '../../Constants';
import { mapPhotoMappings } from '../AnalysisPage/mapPhotos';
import TeamLogo from '../../components/TeamLogo';
import Loading from '../../components/Loading';
import { getVetoHistory } from '../../services/MatchService';
import { Team } from '../../models/Team';
import { PlayerStats } from '../../models/PlayerStats';
import { VetoHistoryEntry } from '../../models/VetoHistory';
import { MapRecord } from './PreMatch2Maps';
import {
    FORMATS, Format, Move, Outcome, Overrides, Side, TeamModel, VetoSetup,
    buildTeamModel, choiceProbabilities, currentPool, defaultFormat, defaultWinChance, evaluate,
    nextStep, playedMap, preferenceValues, rankOurMoves, remainingMaps,
} from './vetoModel';
import panel from './PreMatch2.module.css';
import styles from './PreMatch2Veto.module.css';

interface Props {
    team1: Team;
    team2: Team;
    division: string;
    /** teamId -> the five most recent players; their earlier teams' vetoes feed the prediction. */
    rosters: Record<string, PlayerStats[]>;
    /** teamId -> map -> won/played, for the default win chance per map. */
    records: Record<string, Map<string, MapRecord>>;
}

type MapMode = 'win' | 'pref';
interface PoolPrefs { order: string[]; off: string[] }

const pctText = (p: number) => `${Math.round(p * 100)}%`;
/** Differences below this are within the noise of the per-map estimates. */
const TIE = 0.005;
const mapLabel = (m: string) => mapMappings.get(`de_${m.toLowerCase()}`) ?? m;
const mapPhoto = (m: string) => mapPhotoMappings[`de_${m.toLowerCase()}`];

/** A season-weighted record for one map: this season counts twice, all-time once. */
const recordFor = (records: Map<string, MapRecord> | undefined, map: string) => {
    const r = records?.get(map.toLowerCase());
    return r ? { won: r.won + r.seasonWon, played: r.played + r.seasonPlayed } : undefined;
};

// The viewer's own map pool is a per-browser convenience; storage can throw in private windows.
const prefsKey = (teamId: string) => `prematch2.vetoPool.${teamId}`;
const loadPrefs = (teamId: string): PoolPrefs | null => {
    try {
        const raw = localStorage.getItem(prefsKey(teamId));
        return raw ? JSON.parse(raw) as PoolPrefs : null;
    } catch { return null; }
};
const savePrefs = (teamId: string, prefs: PoolPrefs) => {
    try { localStorage.setItem(prefsKey(teamId), JSON.stringify(prefs)); } catch { /* not remembered */ }
};
/** Stored prefs fitted to today's pool: rotated-out maps dropped, new ones appended. */
const fitPrefs = (prefs: PoolPrefs, pool: string[]): PoolPrefs => ({
    order: [...prefs.order.filter(m => pool.includes(m)), ...pool.filter(m => !prefs.order.includes(m))],
    off: prefs.off.filter(m => pool.includes(m)),
});

/**
 * /prematch veto tab. You say which team you are and who opens; the tool plays their side from
 * their history and shows, for each of your moves, where the veto is likely to end. Clicking a
 * map on their turn records what they really did, so it can run alongside the live veto.
 */
const PreMatch2Veto: React.FC<Props> = ({ team1, team2, division, rosters, records }) => {
    const { user } = useContext(AuthContext);

    const [histories, setHistories] = useState<Record<string, VetoHistoryEntry[]> | null>(null);
    const [failed, setFailed] = useState(false);

    // Default to the viewer's own team when they are on one of the rosters.
    const viewerTeam = useMemo(() => {
        if (!user) return null;
        return [team1, team2].find(t => (rosters[t.teamId] ?? []).some(p => p.steamID === user.steamID))?.teamId ?? null;
    }, [user, team1, team2, rosters]);

    const [usId, setUsId] = useState<string>(viewerTeam ?? team1.teamId);
    const [format, setFormat] = useState<Format>(() => defaultFormat(division));
    const [opener, setOpener] = useState<Side>('us');
    const [moves, setMoves] = useState<Move[]>([]);
    // Per-map overrides of our win chance, set with the +/- on each card.
    const [adjust, setAdjust] = useState<Record<string, number>>({});
    const [mapMode, setMapMode] = useState<MapMode>('win');
    const [prefs, setPrefs] = useState<PoolPrefs | null>(null);
    // Which of the opponent's past vetoes the viewer has switched on or off.
    const [overrides, setOverrides] = useState<Overrides>({});

    useEffect(() => { if (viewerTeam) setUsId(viewerTeam); }, [viewerTeam]);

    const rosterKey = [team1, team2].map(t => (rosters[t.teamId] ?? []).map(p => p.steamID).join(',')).join('|');
    useEffect(() => {
        let cancelled = false;
        setHistories(null);
        setFailed(false);
        (async () => {
            try {
                const [h1, h2] = await Promise.all([team1, team2].map(t =>
                    getVetoHistory(t.teamId, (rosters[t.teamId] ?? []).map(p => p.steamID))));
                if (!cancelled) setHistories({ [team1.teamId]: h1, [team2.teamId]: h2 });
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => { cancelled = true; };
        // rosterKey stands in for rosters: the object identity changes on every page render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team1.teamId, team2.teamId, rosterKey]);

    const us = usId === team2.teamId ? team2 : team1;
    const them = us === team1 ? team2 : team1;

    const pool = useMemo(
        () => (histories ? currentPool(Object.values(histories), currentSeason) : []),
        [histories],
    );
    const themModel = useMemo<TeamModel | null>(
        () => (histories ? buildTeamModel(histories[them.teamId] ?? [], pool, currentSeason, overrides) : null),
        [histories, them.teamId, pool, overrides],
    );

    const recordChance = useMemo(() => {
        const out: Record<string, number> = {};
        pool.forEach(m => {
            out[m] = adjust[m] ?? defaultWinChance(recordFor(records[us.teamId], m), recordFor(records[them.teamId], m));
        });
        return out;
    }, [pool, adjust, records, us.teamId, them.teamId]);

    // A saved pool wins; otherwise start from the record order with every map in play.
    useEffect(() => {
        if (pool.length === 0) return;
        const saved = loadPrefs(usId);
        if (saved) {
            setPrefs(fitPrefs(saved, pool));
            setMapMode('pref');
        } else {
            setPrefs(null);
            setMapMode('win');
        }
    }, [usId, pool]);

    const activePrefs = useMemo<PoolPrefs>(
        () => prefs ?? { order: [...pool].sort((a, b) => recordChance[b] - recordChance[a]), off: [] },
        [prefs, pool, recordChance],
    );
    const updatePrefs = (next: PoolPrefs) => { setPrefs(next); savePrefs(usId, next); };

    const values = useMemo(
        () => (mapMode === 'pref' ? preferenceValues(activePrefs.order, activePrefs.off) : recordChance),
        [mapMode, activePrefs, recordChance],
    );

    const setup = useMemo<VetoSetup | null>(
        () => (themModel ? { format, opener, pool, winChance: values, objective: mapMode, them: themModel } : null),
        [format, opener, pool, values, mapMode, themModel],
    );

    // A new perspective or format invalidates the veto so far.
    useEffect(() => { setMoves([]); }, [usId, format, opener]);
    useEffect(() => { setAdjust({}); setOverrides({}); }, [usId]);

    const step = useMemo(() => (setup ? nextStep(setup, moves) : null), [setup, moves]);
    const left = useMemo(() => (setup ? remainingMaps(pool, moves) : []), [setup, pool, moves]);
    const outcome = useMemo(() => (setup ? evaluate(setup, moves) : null), [setup, moves]);
    const ranked = useMemo(() => (setup ? rankOurMoves(setup, moves) : []), [setup, moves]);
    const theirProbs = useMemo(() => {
        if (!setup || !step || step.side !== 'them') return null;
        return choiceProbabilities(step.action === 'ban' ? setup.them.ban : setup.them.pick, left);
    }, [setup, step, left]);

    if (failed) {
        return <div className={panel.panel} style={{ marginTop: 18 }}><div className={styles.empty}>Kunne ikke hente veto-historik.</div></div>;
    }
    if (!histories || !setup || !themModel || !outcome) {
        return <div className="text-center text-light" style={{ marginTop: 18 }}><Loading /></div>;
    }

    const pref = mapMode === 'pref';
    const sideName = (s: Side) => (s === 'us' ? us.teamName : them.teamName);
    const isBo1 = FORMATS[format].series === 'bo1';
    const played = playedMap(setup, moves);
    const playable = activePrefs.order.filter(m => !activePrefs.off.includes(m));
    /** In pref mode the number that matters: how likely the veto lands on a map we play. */
    const onOurMaps = (o: Outcome) => playable.reduce((s, m) => s + (o.finalMap[m] ?? 0), 0);
    const headline = (o: Outcome) => (pref ? onOurMaps(o) : o.win);

    // The ban-three-pick-one bo1 is a different game from the others: the bans only decide which
    // four maps the other team picks from, so say that up front.
    const rule = format === 'bo1'
        ? (opener === 'us'
            ? `I banner 3 maps, og ${them.teamName} picker 1 af de sidste 4. Brug jeres bans til at styre, hvilke 4 de kan vælge imellem.`
            : `${them.teamName} banner 3 maps, og I picker 1 af de sidste 4.`)
        : null;
    const play = (map: string) => {
        if (!step || !left.includes(map)) return;
        setMoves(ms => [...ms, { side: step.side, action: step.action, map }]);
    };
    const expectedTheirs = theirProbs
        ? Object.entries(theirProbs).sort((a, b) => b[1] - a[1])[0]?.[0]
        : undefined;

    const autoFill = () => {
        let ms = [...moves];
        for (let s = nextStep(setup, ms); s; s = nextStep(setup, ms)) {
            const avail = remainingMaps(pool, ms);
            let map: string;
            if (s.side === 'us') {
                map = rankOurMoves(setup, ms)[0].map;
            } else {
                const probs = choiceProbabilities(s.action === 'ban' ? themModel.ban : themModel.pick, avail);
                map = avail.reduce((a, b) => (probs[b] > probs[a] ? b : a));
            }
            ms = [...ms, { side: s.side, action: s.action, map }];
        }
        setMoves(ms);
    };

    const nudge = (map: string, delta: number) => {
        setAdjust(a => ({ ...a, [map]: Math.min(0.95, Math.max(0.05, (a[map] ?? recordChance[map]) + delta)) }));
    };

    const clearBest = ranked.length > 1 && ranked[0].outcome.win - ranked[1].outcome.win >= TIE;
    const steps = FORMATS[format].steps;
    const otherSide: Side = opener === 'us' ? 'them' : 'us';
    const ownCount = themModel.used.filter(u => u.entry.own).length;
    const playerCount = themModel.used.length - ownCount;
    const finalRows = Object.entries(outcome.finalMap).filter(([, p]) => p >= 0.005).sort((a, b) => b[1] - a[1]);
    const rankOf = (m: string) => (activePrefs.off.includes(m) ? null : playable.indexOf(m) + 1);

    return (
        <div className={styles.wrap}>
            <div className={panel.panel}>
                <div className={panel.panelHead}>
                    <span className={panel.panelTitle}>Veto-simulator</span>
                    <span className={panel.panelSub}>
                        {themModel.used.length === 0
                            ? `Ingen veto-historik for ${them.teamName} — forudsigelsen er et gæt`
                            : `${them.teamName}: ${ownCount} egne vetoer · ${playerCount} fra spillernes tidligere hold`}
                    </span>
                </div>
                <div className={styles.setup}>
                    <div className={styles.setupGroup}>
                        <span className={styles.setupLabel}>Vi er</span>
                        <div className={styles.segment}>
                            {[team1, team2].map(t => (
                                <button key={t.teamId} type="button"
                                    className={`${styles.segBtn} ${usId === t.teamId ? styles.segActive : ''}`}
                                    onClick={() => setUsId(t.teamId)}>
                                    <TeamLogo logo={t.teamLogo} name={t.teamName} className={styles.segCrest} />
                                    {t.teamName}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.setupGroup}>
                        <span className={styles.setupLabel}>Format</span>
                        <div className={styles.segment}>
                            {(Object.keys(FORMATS) as Format[]).map(f => (
                                <button key={f} type="button"
                                    className={`${styles.segBtn} ${format === f ? styles.segActive : ''}`}
                                    onClick={() => setFormat(f)}>
                                    {FORMATS[f].label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.setupGroup}>
                        <span className={styles.setupLabel}>Starter vetoen</span>
                        <div className={styles.segment}>
                            {(['us', 'them'] as Side[]).map(s => (
                                <button key={s} type="button"
                                    className={`${styles.segBtn} ${opener === s ? styles.segActive : ''}`}
                                    onClick={() => setOpener(s)}>
                                    {sideName(s)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.setupGroup}>
                        <span className={styles.setupLabel}>Vores maps</span>
                        <div className={styles.segment}>
                            <button type="button" className={`${styles.segBtn} ${!pref ? styles.segActive : ''}`}
                                onClick={() => setMapMode('win')}>
                                Efter winrate
                            </button>
                            <button type="button" className={`${styles.segBtn} ${pref ? styles.segActive : ''}`}
                                onClick={() => { setMapMode('pref'); if (!prefs) updatePrefs(activePrefs); }}>
                                Vælg selv
                            </button>
                        </div>
                    </div>
                </div>

                {pref && (
                    <PoolEditor
                        prefs={activePrefs}
                        onChange={updatePrefs}
                        onReset={() => updatePrefs({ order: [...pool].sort((a, b) => recordChance[b] - recordChance[a]), off: [] })}
                    />
                )}

                <ol className={styles.rail}>
                    {steps.map((s, i) => {
                        const side = s.first ? opener : otherSide;
                        const done = moves[i];
                        const current = i === moves.length;
                        return (
                            <li key={i} className={`${styles.railStep} ${current ? styles.railCurrent : ''} ${done ? styles.railDone : ''} ${side === 'us' ? styles.railUs : styles.railThem}`}>
                                <span className={styles.railNum}>{i + 1}</span>
                                <span className={styles.railWho}>{side === 'us' ? 'Os' : 'Dem'} · {s.action === 'ban' ? 'ban' : 'pick'}</span>
                                <span className={styles.railMap}>{done ? mapLabel(done.map) : current ? 'nu' : ''}</span>
                            </li>
                        );
                    })}
                    <li className={`${styles.railStep} ${!step ? styles.railCurrent : ''}`}>
                        <span className={styles.railNum}>★</span>
                        <span className={styles.railWho}>{isBo1 ? 'Spilles' : 'Decider'}</span>
                        <span className={styles.railMap}>{played ? mapLabel(played) : ''}</span>
                    </li>
                </ol>

                {rule && <div className={styles.rule}>{rule}</div>}
                <div className={styles.turn}>
                    {step ? (
                        step.side === 'us'
                            ? <>Jeres tur: <b>{step.action === 'ban' ? 'ban' : 'pick'}</b> et map. {pref
                                ? `Tallet er chancen for, at ${isBo1 ? 'vetoen ender' : 'decideren ender'} på et af jeres maps, hvis I vælger det.`
                                : `Tallet er jeres chance for at vinde${isBo1 ? '' : ' serien'}, hvis I vælger det.`}
                                {!clearBest && ' Flere valg er lige gode her; se hvor vetoen ender.'}</>
                            : <><b>{them.teamName}</b> skal {step.action === 'ban' ? 'banne' : 'picke'}. Klik det map de valgte, eller brug det forventede.</>
                    ) : (
                        <>Vetoen er færdig. {isBo1 ? `I spiller ${mapLabel(played ?? '')}.` : `Decider: ${mapLabel(played ?? '')}.`}</>
                    )}
                </div>

                <div className={styles.body}>
                    <div className={styles.grid}>
                        {pool.map(map => {
                            const move = moves.find(m => m.map === map);
                            const available = !move && !!step;
                            const isFinal = played === map;
                            const ourOption = step?.side === 'us' ? ranked.find(r => r.map === map) : undefined;
                            // Only call a move "recommended" when it clearly beats the runner-up;
                            // a half-point edge is noise in the estimates, not advice.
                            const best = ourOption && ranked[0]?.map === map && clearBest;
                            const behind = ourOption ? headline(ranked[0].outcome) - headline(ourOption.outcome) : 0;
                            const theirP = theirProbs?.[map];
                            const topFinal = ourOption && isBo1 && step?.action === 'ban'
                                ? Object.entries(ourOption.outcome.finalMap).sort((a, b) => b[1] - a[1]).slice(0, 2)
                                : [];
                            const rank = rankOf(map);
                            return (
                                <div key={map}
                                    className={`${styles.card} ${move && !isFinal ? styles.cardUsed : ''} ${available ? styles.cardLive : ''} ${best || (expectedTheirs === map) ? styles.cardHighlight : ''} ${isFinal ? styles.cardFinal : ''}`}
                                    onClick={() => available && play(map)}
                                    role={available ? 'button' : undefined}
                                    tabIndex={available ? 0 : undefined}
                                    onKeyDown={e => { if (available && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); play(map); } }}>
                                    {mapPhoto(map) && <img src={mapPhoto(map)} alt="" className={styles.cardImg} />}
                                    <div className={styles.cardShade} />
                                    <div className={styles.cardTop}>
                                        <span className={styles.cardName}>{mapLabel(map)}</span>
                                        {best && <span className={styles.tagBest}><AutoAwesome sx={{ fontSize: 12 }} />Anbefalet</span>}
                                        {expectedTheirs === map && <span className={styles.tagExpected}>Forventet</span>}
                                    </div>
                                    <div className={styles.cardMid}>
                                        {move && (
                                            <span className={`${styles.stamp} ${move.action === 'ban' ? styles.stampBan : styles.stampPick}`}>
                                                {move.action === 'ban' ? 'Ban' : 'Pick'} · {move.side === 'us' ? 'os' : 'dem'}
                                            </span>
                                        )}
                                        {isFinal && <span className={`${styles.stamp} ${styles.stampFinal}`}>{isBo1 ? 'Spilles' : 'Decider'}</span>}
                                        {ourOption && (
                                            <>
                                                <span className={styles.bigNum}>
                                                    {pctText(headline(ourOption.outcome))}
                                                    {!best && (
                                                        <span className={styles.delta}>
                                                            {behind < TIE ? '≈ bedste' : `−${Math.round(behind * 100)} pt`}
                                                        </span>
                                                    )}
                                                </span>
                                                {topFinal.length > 0 && (
                                                    <span className={styles.cardHint}>
                                                        ender på {topFinal.map(([m, p]) => `${mapLabel(m)} ${pctText(p)}`).join(' · ')}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                        {theirP !== undefined && !move && (
                                            <>
                                                <span className={`${styles.bigNum} ${styles.bigNumThem}`}>{pctText(theirP)}</span>
                                                <span className={styles.cardHint}>chance for {step?.action === 'ban' ? 'ban' : 'pick'}</span>
                                            </>
                                        )}
                                    </div>
                                    {pref ? (
                                        <div className={styles.cardFoot}>
                                            <span className={styles.footLabel}>Hos jer</span>
                                            <span className={`${styles.footRank} ${rank ? '' : styles.footOff}`}>
                                                {rank ? `#${rank}` : 'spiller ikke'}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className={styles.cardFoot} onClick={e => e.stopPropagation()}>
                                            <span className={styles.footLabel}>Jeres chance på mappet</span>
                                            <button type="button" className={styles.nudge} onClick={() => nudge(map, -0.05)} aria-label={`Sænk ${mapLabel(map)}`}>−</button>
                                            <span className={`${styles.footValue} ${adjust[map] !== undefined ? styles.footEdited : ''}`}>{pctText(recordChance[map])}</span>
                                            <button type="button" className={styles.nudge} onClick={() => nudge(map, 0.05)} aria-label={`Hæv ${mapLabel(map)}`}>+</button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <aside className={styles.side}>
                        <div className={styles.sideBlock}>
                            <span className={styles.sideLabel}>
                                {pref
                                    ? (isBo1 ? 'Chance for at ende på jeres maps' : 'Decider på jeres maps')
                                    : (isBo1 ? 'Chance for at vinde' : 'Chance for at vinde serien')}
                            </span>
                            <span className={styles.sideBig}>{pctText(headline(outcome))}</span>
                            <span className={styles.sideNote}>hvis I følger anbefalingerne og {them.teamName} vetoer som de plejer</span>
                        </div>
                        <div className={styles.sideBlock}>
                            <span className={styles.sideLabel}>{isBo1 ? 'Map der ender med at blive spillet' : 'Decider'}</span>
                            {finalRows.map(([m, p]) => (
                                <div className={styles.distRow} key={m}>
                                    <span className={styles.distName}>
                                        {pref && <span className={styles.distRank}>{rankOf(m) ? `#${rankOf(m)}` : '–'}</span>}
                                        {mapLabel(m)}
                                    </span>
                                    <span className={styles.distBar}><i style={{ width: `${p * 100}%` }} /></span>
                                    <span className={styles.distPct}>{pctText(p)}</span>
                                </div>
                            ))}
                        </div>
                        <div className={styles.actions}>
                            {step?.side === 'them' && expectedTheirs && (
                                <button type="button" className={styles.actionPrimary} onClick={() => play(expectedTheirs)}>
                                    Brug forventet ({mapLabel(expectedTheirs)})
                                </button>
                            )}
                            {step && (
                                <button type="button" className={styles.action} onClick={autoFill}>
                                    <FastForward sx={{ fontSize: 16 }} />Simulér resten
                                </button>
                            )}
                            <button type="button" className={styles.action} disabled={moves.length === 0}
                                onClick={() => setMoves(ms => ms.slice(0, -1))}>
                                <Undo sx={{ fontSize: 16 }} />Fortryd
                            </button>
                            <button type="button" className={styles.action} disabled={moves.length === 0}
                                onClick={() => setMoves([])}>
                                <RestartAlt sx={{ fontSize: 16 }} />Nulstil
                            </button>
                        </div>
                    </aside>
                </div>
            </div>

            <WhyPanel
                them={them}
                model={themModel}
                pool={pool}
                roster={rosters[them.teamId] ?? []}
                overrides={overrides}
                onOverride={(ids, include) => setOverrides(o => {
                    const next = { ...o };
                    ids.forEach(id => { next[id] = include; });
                    return next;
                })}
                onResetOverrides={() => setOverrides({})}
            />
        </div>
    );
};

/**
 * The viewer's own map pool: drag (or use the arrows) to set the order, click a map to say you
 * don't play it. The simulator then steers towards maps you play, your top ones first.
 */
const PoolEditor: React.FC<{ prefs: PoolPrefs; onChange: (p: PoolPrefs) => void; onReset: () => void }> = ({
    prefs, onChange, onReset,
}) => {
    const [dragging, setDragging] = useState<string | null>(null);
    const playedMaps = prefs.order.filter(m => !prefs.off.includes(m));
    const offMaps = prefs.order.filter(m => prefs.off.includes(m));
    // Keep "don't play" maps at the back so the rank numbers read straight down the list.
    const commit = (order: string[], off = prefs.off) =>
        onChange({ order: [...order.filter(m => !off.includes(m)), ...order.filter(m => off.includes(m))], off });

    const move = (map: string, delta: number) => {
        const order = [...playedMaps];
        const i = order.indexOf(map);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= order.length) return;
        [order[i], order[j]] = [order[j], order[i]];
        commit([...order, ...offMaps]);
    };
    const dropOn = (target: string) => {
        if (!dragging || dragging === target) return;
        const order = playedMaps.filter(m => m !== dragging);
        order.splice(order.indexOf(target), 0, dragging);
        commit([...order, ...offMaps]);
    };
    const toggle = (map: string) => {
        const off = prefs.off.includes(map) ? prefs.off.filter(m => m !== map) : [...prefs.off, map];
        commit(prefs.order, off);
    };

    return (
        <div className={styles.poolEditor}>
            <div className={styles.poolHead}>
                <span className={styles.setupLabel}>Jeres map-pool</span>
                <span className={styles.poolHint}>Træk for at sortere · klik et map, I ikke spiller · gemmes i denne browser</span>
                <button type="button" className={styles.linkBtn} onClick={onReset}>Nulstil</button>
            </div>
            <ol className={styles.poolList}>
                {playedMaps.map((m, i) => (
                    <li key={m}
                        className={`${styles.poolItem} ${dragging === m ? styles.poolDragging : ''}`}
                        draggable
                        onDragStart={() => setDragging(m)}
                        onDragEnd={() => setDragging(null)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); dropOn(m); setDragging(null); }}>
                        <DragIndicator sx={{ fontSize: 16 }} className={styles.poolGrip} />
                        <span className={styles.poolRank}>#{i + 1}</span>
                        <button type="button" className={styles.poolName} onClick={() => toggle(m)} title="Klik: vi spiller ikke mappet">
                            {mapLabel(m)}
                        </button>
                        <span className={styles.poolArrows}>
                            <button type="button" onClick={() => move(m, -1)} disabled={i === 0} aria-label={`Flyt ${mapLabel(m)} op`}>
                                <KeyboardArrowUp sx={{ fontSize: 16 }} />
                            </button>
                            <button type="button" onClick={() => move(m, 1)} disabled={i === playedMaps.length - 1} aria-label={`Flyt ${mapLabel(m)} ned`}>
                                <KeyboardArrowDown sx={{ fontSize: 16 }} />
                            </button>
                        </span>
                    </li>
                ))}
                {offMaps.map(m => (
                    <li key={m} className={`${styles.poolItem} ${styles.poolItemOff}`}>
                        <span className={styles.poolRank}>–</span>
                        <button type="button" className={styles.poolName} onClick={() => toggle(m)} title="Klik: vi spiller mappet">
                            {mapLabel(m)}
                        </button>
                        <span className={styles.poolOffLabel}>spiller ikke</span>
                    </li>
                ))}
            </ol>
        </div>
    );
};

interface SourceGroup {
    key: string;
    name: string;
    season: number;
    own: boolean;
    overlap: number;
    weight: number;
    entries: TeamModel['all'];
}

const shortDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: '2-digit' });
};

/**
 * The evidence behind the prediction: their tendencies from a full pool, and every veto that was
 * found, grouped by team and season. Each group opens into its vetoes, and any veto (or a whole
 * group) can be switched off or forced on, so a veto from a squad that no longer exists stops
 * steering the prediction.
 */
const WhyPanel: React.FC<{
    them: Team;
    model: TeamModel;
    pool: string[];
    roster: PlayerStats[];
    overrides: Overrides;
    onOverride: (matchIds: string[], include: boolean) => void;
    onResetOverrides: () => void;
}> = ({ them, model, pool, roster, overrides, onOverride, onResetOverrides }) => {
    const [open, setOpen] = useState<string | null>(null);
    const firstBan = choiceProbabilities(model.ban, pool);
    const firstPick = choiceProbabilities(model.pick, pool);
    const rows = [...pool].sort((a, b) => firstBan[b] - firstBan[a]);
    const names = new Map(roster.map(p => [p.steamID, p.account?.playerName ?? p.steamID]));

    // One line per team-season, so "3/5 fra MASQ S32" reads as one source rather than eight rows.
    const groups = new Map<string, SourceGroup>();
    model.all.forEach(u => {
        const key = `${u.entry.subjectTeamId}:${u.entry.subjectName}:${u.entry.season}`;
        const g = groups.get(key) ?? {
            key, name: u.entry.subjectName, season: u.entry.season, own: u.entry.own, overlap: 0, weight: 0, entries: [],
        };
        g.overlap = Math.max(g.overlap, u.entry.overlap);
        g.weight += u.weight;
        g.entries.push(u);
        groups.set(key, g);
    });
    const sources = Array.from(groups.values())
        .sort((a, b) => (b.weight - a.weight) || (b.season - a.season));
    const edited = Object.keys(overrides).length > 0;

    return (
        <div className={panel.panel}>
            <div className={panel.panelHead}>
                <span className={panel.panelTitle}>Hvorfor? {them.teamName}s veto-profil</span>
                <span className={panel.panelSub}>{model.all.length} vetoer fundet · sidste 4 sæsoner</span>
            </div>
            <div className={styles.why}>
                <div>
                    <div className={styles.whyHead}>
                        <span>Map</span><span>Første ban</span><span>Pick</span>
                    </div>
                    {rows.map(m => (
                        <div className={styles.whyRow} key={m}>
                            <span className={styles.distName}>{mapLabel(m)}</span>
                            <span className={styles.whyCell}>
                                <span className={`${styles.distBar} ${styles.barBan}`}><i style={{ width: `${firstBan[m] * 100}%` }} /></span>
                                <span className={styles.distPct}>{pctText(firstBan[m])}</span>
                            </span>
                            <span className={styles.whyCell}>
                                <span className={`${styles.distBar} ${styles.barPick}`}><i style={{ width: `${firstPick[m] * 100}%` }} /></span>
                                <span className={styles.distPct}>{model.pickEvidence > 0 ? pctText(firstPick[m]) : '–'}</span>
                            </span>
                        </div>
                    ))}
                    <p className={styles.whyNote}>
                        Chancen for hvert map, hvis de skulle vælge fra hele poolen. Undervejs regnes der kun på de maps, der er tilbage.
                    </p>
                </div>
                <div>
                    <div className={styles.whyHead}>
                        <span>Kilde</span><span>Vetoer</span>
                        <span>{edited
                            ? <button type="button" className={styles.linkBtn} onClick={onResetOverrides}>Nulstil valg</button>
                            : 'Tæller'}</span>
                    </div>
                    {sources.length === 0 && <p className={styles.whyNote}>Ingen tidligere vetoer fundet.</p>}
                    {sources.map(g => {
                        const counted = g.entries.filter(u => u.weight > 0).length;
                        const isOpen = open === g.key;
                        const ids = g.entries.map(u => u.entry.matchId);
                        return (
                            <div key={g.key} className={`${styles.source} ${counted === 0 ? styles.sourceOff : ''}`}>
                                <div className={styles.whyRow}>
                                    <button type="button" className={styles.sourceName} onClick={() => setOpen(isOpen ? null : g.key)}>
                                        {isOpen ? <ExpandMore sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />}
                                        {g.name} <span className={styles.sourceSeason}>S{g.season}</span>
                                        <span className={g.own ? styles.badgeOwn : styles.badgePlayers}
                                            title={g.own ? 'Vetoer fra samme hold-id' : 'Vetoer fra et andet hold med nogle af de nuværende spillere'}>
                                            {g.own ? `eget hold · ${g.overlap}/5` : `${g.overlap}/5 spillere`}
                                        </span>
                                    </button>
                                    <span className={styles.whyNum}>{counted}/{g.entries.length}</span>
                                    <label className={styles.check}>
                                        <input type="checkbox" checked={counted > 0}
                                            onChange={e => onOverride(ids, e.target.checked)} />
                                        {counted === 0 && g.entries.every(u => u.autoWeight === 0) && !g.entries.some(u => overrides[u.entry.matchId] !== undefined)
                                            ? <span className={styles.checkNote}>for få af spillerne</span>
                                            : null}
                                    </label>
                                </div>
                                {isOpen && (
                                    <ul className={styles.vetoList}>
                                        {g.entries.map(u => {
                                            const e = u.entry;
                                            const shared = (e.sharedPlayers ?? []).map(id => names.get(id) ?? id);
                                            return (
                                                <li key={e.matchId} className={`${styles.vetoItem} ${u.weight > 0 ? '' : styles.vetoItemOff}`}>
                                                    <div className={styles.vetoMeta}>
                                                        <label className={styles.check}>
                                                            <input type="checkbox" checked={u.weight > 0}
                                                                onChange={ev => onOverride([e.matchId], ev.target.checked)} />
                                                        </label>
                                                        <span className={styles.vetoDate}>{shortDate(e.matchTime)}</span>
                                                        <span>vs <b>{e.opponentName || 'ukendt'}</b></span>
                                                        <span className={styles.sourceSeason}>{e.division}</span>
                                                        <span className={styles.vetoPlayers}>
                                                            {shared.length > 0 ? shared.join(', ') : 'ingen af de nuværende spillere'}
                                                        </span>
                                                    </div>
                                                    <div className={styles.vetoSteps}>
                                                        {e.steps.filter(s => s.kind !== 'side').map((s, i) => (
                                                            <span key={i} className={`${styles.chip} ${
                                                                s.kind === 'leftover' ? styles.chipPlayed
                                                                    : s.actor !== 'subject' ? styles.chipOpp
                                                                        : s.kind === 'ban' ? styles.chipBan : styles.chipPick}`}
                                                                title={s.actor === 'subject' ? e.subjectName : e.opponentName}>
                                                                {s.kind === 'ban' ? '✕' : s.kind === 'pick' ? '✓' : '★'} {mapLabel(s.map)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                    <p className={styles.whyNote}>
                        En veto tæller mere, jo flere af de nuværende fem der spillede den, og jo nyere den er. Vetoer med
                        højst én af de nuværende spillere tæller ikke, heller ikke under samme hold-navn. Slå en veto til
                        eller fra, hvis I ved bedre. Farvede chips er {them.teamName}s valg, grå er modstanderens.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PreMatch2Veto;
